import MorningCheckIn from "@/components/routine/MorningCheckIn";
import BeforeYouSleep from "@/components/BeforeYouSleep";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import ResetSheet from "@/components/ResetSheet";
import CoachCheckIn from "@/components/routine/CoachCheckIn";
import { FeelingChips, TodayForYou } from "@/components/routine/TodayForYou";
import WalkingInto from "@/components/routine/WalkingInto";
import MyRoutine from "@/components/routine/MyRoutine";
import EveningCheckIn from "@/components/routine/EveningCheckIn";
import MyRhythm from "@/components/routine/MyRhythm";
import SimplerOffer from "@/components/routine/SimplerOffer";
import WeeklyNote from "@/components/routine/WeeklyNote";
import ImmersionFollowUp from "@/components/routine/ImmersionFollowUp";
import ImmersionWeek from "@/components/routine/ImmersionWeek";
import MissionCheckIn from "@/components/routine/MissionCheckIn";
import GoToReset, { PlanReset, SmileStart } from "@/components/routine/GoToReset";
import CoachPlayCard from "@/components/CoachPlayCard";
import InviteNudges from "@/components/routine/InviteNudges";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const localDate = () => new Date().toLocaleDateString("en-CA");
const isEvening = () => { const h = new Date().getHours(); return h >= 17 || h < 4; };

export default function DailyRoutine() {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [coach, setCoach] = useState(null);
  const [feeling, setFeeling] = useState(() => sessionStorage.getItem(`xc_feel_${localDate()}`));
  const [grat, setGrat] = useState({ open: false, text: "" });
  const [reset, setReset] = useState(null);
  const [offer, setOffer] = useState(null);
  const [weekly, setWeekly] = useState(null);

  const refresh = useCallback(async () => {
    const [t, w] = await Promise.all([api.get("/routine/today", { params: { local_date: localDate() } }), api.get("/routine/week", { params: { local_date: localDate() } })]);
    setToday(t.data); setWeek(w.data);
    setGrat((g) => ({ ...g, text: g.text || t.data.gratitude || "" }));
  }, []);
  const loadCoach = useCallback((f, play = false) => {
    api.post("/routine/coach", { local_hour: new Date().getHours(), local_date: localDate(), feeling: f || null })
      .then((r) => setCoach({ ...r.data, playKey: play ? Date.now() : 0 })).catch(() => {});
  }, []);
  useEffect(() => {
    refresh().catch(() => toast.error("Couldn't load your routine."));
    if (feeling || isEvening()) loadCoach(feeling);
    api.get("/routine/simplify").then((r) => setOffer(r.data.offer)).catch(() => {});
    api.get("/routine/weekly-note", { params: { local_date: localDate() } }).then((r) => setWeekly(r.data.note)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const answerOffer = async (action) => {
    try { await api.post("/routine/simplify", { action }); } catch { toast.error("Couldn't save that. Please try again."); return; }
    setOffer(null);
    toast(action === "accept" ? "Your routine is lighter now. You can go back to the full version any time." : action === "later" ? "I'll ask again in a couple of weeks." : "Keeping your routine as it is.");
    refresh();
  };

  const open = async (p) => {
    if (p.route?.startsWith("/resets?ctx=")) { setReset({ ctx: p.route.split("=")[1], next: p.next }); return; }
    navigate(p.route);
  };
  const onTap = async (it) => {
    if (it.kind === "gratitude") { setGrat((g) => ({ ...g, open: !g.open })); return; }
    if (it.kind === "evening") { document.querySelector('[data-testid="routine-evening"]')?.scrollIntoView({ behavior: "smooth" }); return; }
    if (it.kind === "link" && !it.completed) {
      await api.post("/routine/toggle", { id: it.id, local_date: localDate() }).catch(() => {});
      if (it.link === "/tonight-sleep") {
        const { data } = await api.get("/tonight");
        navigate(data.installation ? `/inner/${data.installation.id}?mode=sleep` : "/inner");
      } else navigate(it.link);
      return;
    }
    const { data } = await api.post("/routine/toggle", { id: it.id, local_date: localDate() }); setToday(data); refresh();
  };
  const saveGrat = async () => {
    if (!grat.text.trim()) return;
    const { data } = await api.post("/routine/gratitude", { text: grat.text, local_date: localDate() }); setToday(data); setGrat((g) => ({ ...g, open: false })); refresh();
  };
  const pickFeeling = (f) => { setFeeling(f); sessionStorage.setItem(`xc_feel_${localDate()}`, f); setCoach(null); loadCoach(f, true); };

  if (!today) return <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-40"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></div>;
  const evening = coach ? coach.part === "evening" : isEvening();
  const eveningDone = today.items.some((i) => i.id === "evening-reflection" && i.completed);

  return (
    <div className="min-h-screen bg-background" data-testid="daily-routine-page">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-32">
        <MorningCheckIn />
        <BeforeYouSleep />
        {evening ? <FeelingChips nowOnly value={feeling} onPick={(f) => { setFeeling(f); sessionStorage.setItem(`xc_feel_${localDate()}`, f); }} />
          : <FeelingChips value={feeling} onPick={pickFeeling} />}
        {coach ? <CoachCheckIn line={coach.line} walkthrough={coach.walkthrough} spoken={coach.spoken} speak={coach.speak} playKey={coach.playKey} part={coach.part} />
          : (feeling || evening) ? <div className="h-28 mb-10 flex items-center text-sm text-muted-foreground gap-2" data-testid="routine-coach-loading"><Loader2 className="w-4 h-4 animate-spin" /> Your Coach is looking at your day…</div>
          : <p className="mb-10 -mt-4 text-xs text-muted-foreground" data-testid="routine-coach-waiting">Choose one and your Coach will walk you through today.</p>}
        {coach?.mission_moment && (
          <div className="mb-10 rounded-3xl border border-amber-300/25 bg-amber-300/[0.05] p-5 sm:p-6" data-testid="mission-streak-moment">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300">Three in a row</p>
            <p className="font-serif text-lg sm:text-xl leading-snug mt-2">{coach.mission_moment}</p>
          </div>
        )}
        <InviteNudges />
        {coach?.mission_checkin && <MissionCheckIn checkin={coach.mission_checkin} />}
        {coach?.reset_rec && (
          <div className="mb-10 rounded-3xl border border-white/10 bg-card/40 p-5 sm:p-6" data-testid="routine-reset-rec">
            <p className="text-[10px] uppercase tracking-[0.22em] text-primary">{coach.reset_rec.why}</p>
            <p className="font-serif text-lg sm:text-xl leading-snug mt-2">Let's do this one together before you go.</p>
            <CoachPlayCard play={coach.reset_rec} autoPlay={false} />
          </div>
        )}
        <PlanReset plan={week?.plan} />
        <GoToReset feeling={feeling} />
        {!evening && <SmileStart feeling={feeling} />}
        <SimplerOffer offer={offer} onAnswer={answerOffer} />
        <ImmersionFollowUp />
        <ImmersionWeek />
        <TodayForYou picks={coach?.picks} onOpen={open} />
        {evening && <EveningCheckIn done={eveningDone} onDone={refresh} />}
        <MyRoutine items={today.items} coachAdded={coach?.coach_added} onTap={onTap} onOpen={open}
          onRemove={async (id) => { await api.delete(`/routine/item/${id}`); refresh(); }}
          onAdd={async (label) => { await api.post("/routine/item", { label }); refresh(); }}
          onFull={async (id) => { await api.post(`/routine/item/${id}/full`); refresh(); }}
          grat={{ open: grat.open, text: grat.text, setText: (t) => setGrat((g) => ({ ...g, text: t })), save: saveGrat }} />
        <WalkingInto onOpen={open} />
        <WeeklyNote note={weekly} />
        <MyRhythm week={week} />
      </main>
      <ResetSheet open={!!reset} autoContext={reset?.ctx} next={reset?.next} onClose={() => setReset(null)} />
    </div>
  );
}
